import * as React from "react";
import {
  PrintIcon,
  EmailIcon,
  FacebookIcon,
  TwitterIcon,
  GoogleIcon,
  LinkedInIcon,
  PopoutIcon,
  MenuIcon,
  PrivacyPolicyIcon
} from "./Icons";
import useConfig from "../hooks/useConfig";
import ensureProtocol from "../helpers/ensureProtocol";
import isMobile from "../helpers/isMobile";
import StoreContainer from "../stores/StoreContainer";

interface Props {
  isPopout: boolean;
  isMenuOpen: boolean;
  footerContent?: React.ReactNode;
  handleMenuButtonClick(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void;
  onPopout?(): void;
  onEmail?(): void;
  onPrint?(): void;
}

function HeaderMenu(props: Props) {
  const config = useConfig();
  const items = [];

  if (config.PrintEnabled && props.onPrint) {
    items.push(
      <a
        key="print"
        className="menu-item"
        tabIndex={0}
        onClick={() => props.onPrint()}
        onKeyDown={(e) => {
          return e.keyCode !== 13 || props.onPrint();
        }}
        role="button"
      >
        <span className="menu-item-icon" aria-hidden="true">
          <PrintIcon />
        </span>
        <span className="menu-item-text">{config.MenuPrintText}</span>
      </a>
    );
  }

  if (config.EmailEnabled && props.onEmail) {
    items.push(
      <a
        key="email"
        className="menu-item"
        tabIndex={0}
        onClick={() => props.onEmail()}
        onKeyDown={(e) => {
          return e.keyCode !== 13 || props.onEmail();
        }}
        role="button"
      >
        <span className="menu-item-icon" aria-hidden="true">
          <EmailIcon />
        </span>
        <span className="menu-item-text">{config.MenuEmailTranscriptText}</span>
      </a>
    );
  }

  if (!props.isPopout && config.PopoutButtonEnabled && props.onPopout) {
    items.push(
      <a
        key="popout"
        className="menu-item"
        tabIndex={0}
        onClick={() => props.onPopout()}
        onKeyDown={(e) => {
          return e.keyCode !== 13 || props.onPopout();
        }}
        role="button"
      >
        <span className="menu-item-icon" aria-hidden="true">
          <PopoutIcon />
        </span>

        <span className="menu-item-text">{config.MenuPopoutText}</span>
      </a>
    );
  }

  const socialMediaItems = [];

  if (config.FacebookButtonEnabled) {
    socialMediaItems.push(
      <a
        key="facebook"
        className="menu-item"
        href={ensureProtocol(config.FacebookUrl)}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="menu-item-icon" aria-hidden="true">
          <FacebookIcon />
        </span>
        <span className="menu-item-text">Facebook</span>
      </a>
    );
  }

  if (config.TwitterButtonEnabled) {
    socialMediaItems.push(
      <a
        key="twitter"
        className="menu-item"
        href={ensureProtocol(config.TwitterUrl)}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="menu-item-icon" aria-hidden="true">
          <TwitterIcon />
        </span>
        <span className="menu-item-text">Twitter</span>
      </a>
    );
  }

  if (config.GoogleButtonEnabled) {
    socialMediaItems.push(
      <a
        key="google"
        className="menu-item"
        href={ensureProtocol(config.GoogleUrl)}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="menu-item-icon" aria-hidden="true">
          <GoogleIcon />
        </span>
        <span className="menu-item-text">Google</span>
      </a>
    );
  }

  if (config.LinkedInButtonEnabled) {
    socialMediaItems.push(
      <a
        key="linkedin"
        className="menu-item"
        href={ensureProtocol(config.LinkedInUrl)}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="menu-item-icon" aria-hidden="true">
          <LinkedInIcon />
        </span>
        <span className="menu-item-text">LinkedIn</span>
      </a>
    );
  }

  if (socialMediaItems.length > 0) {
    items.push(
      <div key="social" className="menu-section">
        {socialMediaItems}
      </div>
    );
  }

  if (props.footerContent) {
    items.push(
      <div key="footer" className="footer-content">
        {props.footerContent}
      </div>
    );
  }
  const siteId = StoreContainer.SettingStore.getSiteIdOrDefault();
  const privacyPolicyExceptions = [20069, 20519];
  let privacyPolicyUrl = ensureProtocol("https://velaro.com/privacy-policy");

  if (siteId === 20957) {
    privacyPolicyUrl = ensureProtocol("https://www.iwawine.com/privacy");
  }

  if (privacyPolicyExceptions.indexOf(siteId) === -1) {
    items.push(
      <a
        aria-label="Velaro Privacy Policy"
        key="privacypolicy"
        className="menu-item"
        href={privacyPolicyUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="menu-item-icon" aria-hidden="true">
          <PrivacyPolicyIcon />
        </span>
        <span className="menu-item-text">Privacy Policy</span>
      </a>
    );
  }

  let className = "velaro-header-top-left";
  if (isMobile() && !props.isPopout) {
    className += "-mobile";
  }
  return (
    <>
      {items.length > 0 && (
        <div className={className}>
          <div className="velaro-header-menu-container">
            <button
              onClick={props.handleMenuButtonClick}
              role="button"
              aria-label="Chat Menu"
              aria-expanded={props.isMenuOpen}
              aria-controls="chat-menu"
              id="chat-menu-toggle"
            >
              <MenuIcon color={config.PrimaryTextColor} />
            </button>
            {props.isMenuOpen && (
              <div
                id="chat-menu"
                className="velaro-header-menu"
                role="region"
                aria-labelledby="chat-menu-toggle"
              >
                <div className="velaro-menu-items">{items}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default HeaderMenu;
